import { Box, Button, ProductPrice, Text } from "@vinomofo/components";
import { useEffect, useState } from "react";

import { ButtonPosition } from "../../contexts/cart/types";
import { VinoDataOffer } from "../../types/vino-data-types";
import { isFlagEnabled } from "../../utils/is-flag-enabled";
import styled from "styled-components";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import useOneClickCheckout from "../../hooks/use-one-click-checkout";
import useScreenSize from "../../hooks/use-screen-size";

export interface OfferDetails {
  productId: string;
  price: number;
  rrp?: number;
  priceDescription?: string;
  name: string;
  offer: VinoDataOffer;
  [key: string]: any;
  show: boolean;
  isSoldOut: boolean;
}

const ScrollNavStyle = styled(Box)<{ isMobile?: boolean }>`
  transition-property: transform, visibility;
  transition-duration: 0.3s;
  transition-timing-function: ease-in-out;
  transform: ${({ isMobile }) =>
    isMobile ? null : "translate3d(0, -100%, 0)"};
  visibility: hidden;
  left: 0;
  &.show {
    transition-property: transform;
    transform: ${({ isMobile }) => (isMobile ? null : "translate3d(0, 0, 0)")};
    visibility: visible;
  }
`;

const NameTextStyled = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-right: 15px;
  font-size: 17px;
`;

const ButtonStyled = styled(Button)`
  padding: 12px 30px;
  @media ${({ theme }) => theme.mq.max.mobile} {
    &.btn-is-soldout {
      font-size: 14px;
      padding: 12px 24px;
    }
  }

  &.btn-is-loading {
    color: rgba(255, 255, 255, 0);
    background-color: #48a90f;
    border-color: #48a90f;
    transition: all 0.5s;
    &:hover {
      color: transparent;
    }
    &::before {
      content: "Loading ...";
      color: rgba(255, 255, 255, 0.7);
      position: absolute;
      left: 0;
      right: 0;
      animation: moveUp 0.5s;
    }
  }
`;

const PriceDescriptionStyled = styled(Text)`
  margin-left: 10px;
  color: #aaaaaa;
`;

export const StickyAddToCart = ({
  productId,
  name,
  price,
  rrp,
  priceDescription,
  offer,
  show,
  isSoldOut,
}: OfferDetails): JSX.Element => {
  const { addItem, isLoading } = useCart();
  const {
    isProcessing,
    isBuying,
    isRedirecting: oneClickCheckoutRedirecting,
  } = useOneClickCheckout();
  const { user } = useAuth();
  const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
  const [productLoading, setProductLoading] = useState({});
  const { isMobile } = useScreenSize();
  const hideRRP = isFlagEnabled(offer.displayFlags, "HIDE_RRP");
  const handleAddButtonClick = () => {
    const customerId =
      userAppMeta?.[`BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`];
    const btnPosition: ButtonPosition = isMobile
      ? "Sticky-Bottom"
      : "Sticky-Top";
    setProductLoading((oldState) => ({ ...oldState, [productId]: true }));
    addItem(productId, offer, customerId, btnPosition, () => {
      setProductLoading((oldState) => ({ ...oldState, [productId]: false }));
    });
  };

  // useEffect(() => {
  //   if (productLoading[productId] && !isLoading)
  //     setProductLoading((oldState) => ({ ...oldState, [productId]: false }));
  // }, [isLoading, productLoading, productId]);
  
  const btnLoading = productLoading[productId];
  const isDisabled = isBuying || isProcessing || oneClickCheckoutRedirecting;

  return (
    <ScrollNavStyle
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      position="fixed"
      overflow="hidden"
      top={isMobile ? "auto" : "0"}
      bottom={isMobile ? "0" : "auto"}
      zIndex="15"
      width="100%"
      backgroundColor="#eaeaea"
      borderBottom="solid 1px #dadada"
      paddingLeft="20px"
      paddingRight="20px"
      height={"62px"}
      className={show || isMobile ? "show" : ""}
      id={
        show || isMobile ? "stickyAddToCart_visible" : "stickyAddToCart_hidden"
      }
      isMobile={isMobile}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display={["none", "none", "block"]}>
          <NameTextStyled
            lineHeight="48px"
            verticalAlign="top"
            maxWidth="450px"
            marginRight="15px"
          >
            {name}
          </NameTextStyled>
        </Box>
        {<ProductPrice size="md" price={price} rrp={!hideRRP && rrp} />}
        {priceDescription && (
          <PriceDescriptionStyled fontSize={[0, 1]} fontStyle="italic">
            {priceDescription}
          </PriceDescriptionStyled>
        )}
      </Box>
      <Box position="relative">
        {!isSoldOut ? (
          <ButtonStyled
            variant="primary"
            type="submit"
            maxWidth={200}
            onClick={handleAddButtonClick}
            className={btnLoading ? "btn-is-loading" : ""}
            disabled={btnLoading || isDisabled}
          >
            Add to cart
          </ButtonStyled>
        ) : (
          <ButtonStyled
            variant="primary"
            type="submit"
            maxWidth={250}
            disabled={true}
            className="btn-is-soldout"
          >
            Sorry, Sold Out
          </ButtonStyled>
        )}
      </Box>
    </ScrollNavStyle>
  );
};
